import { useEffect, useRef, useState } from 'react';

const DEFAULT_DEBOUNCE_MS = 180;

type UseDebouncedSearchWithCacheOptions<T> = {
  /**
   * Если false — результат немедленно очищается и поиск не запускается.
   * Используется для отключения хука вне нужного контекста (например, "только в partner-тредах").
   */
  enabled: boolean;
  /**
   * Стабильный ключ для кэширования. Обычно собирается из нормализованного query
   * и доп. контекста (id владельца, тип треда). Хук НЕ нормализует ключ сам —
   * это ответственность caller-а.
   */
  cacheKey: string;
  /**
   * Асинхронный fetcher, возвращающий массив элементов. Может быть нестабильным
   * (создаётся каждый рендер) — хук держит latest-ref и не триггерит ре-запрос
   * на смену ссылки. Зависимости запроса контролируются через `cacheKey`.
   */
  fetcher: () => Promise<T[]>;
  /** Хэндлер ошибки, специфичный для домена (например, logger с контекстом). */
  onError?: (error: unknown) => void;
  /** Задержка debounce. По умолчанию 180мс. */
  debounceMs?: number;
};

type UseDebouncedSearchWithCacheResult<T> = {
  results: T[];
  loading: boolean;
};

/**
 * Универсальный скелет для асинхронного поиска с debounce, in-memory-кэшом
 * и stale-request защитой. Из специфики Activity сюда вынесена общая часть
 * `useActivityMentionUsersSearch` / scope-mention провайдеров.
 *
 * Контракт:
 *  - `enabled === false` → result = [], loading = false, никаких запросов.
 *  - `cacheKey` уже в кэше → result = cached, loading = false, без запроса.
 *  - Иначе → debounce(debounceMs) → fetcher() → cache + setResults.
 *  - Stale-check: при гонке (новый запрос пока летел старый) результат старого игнорируется.
 *
 * Latest-ref для `fetcher` и `onError` — caller может не мемоизировать функции,
 * перезапуск useEffect происходит только при смене `enabled`/`cacheKey`/`debounceMs`.
 */
export function useDebouncedSearchWithCache<T>({
  enabled,
  cacheKey,
  fetcher,
  onError,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: UseDebouncedSearchWithCacheOptions<T>): UseDebouncedSearchWithCacheResult<T> {
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);

  const cacheRef = useRef<Map<string, T[]>>(new Map());
  const requestSeqRef = useRef(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!enabled) {
      setResults([]);
      setLoading(false);
      return;
    }

    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setResults(cached);
      setLoading(false);
      return;
    }

    const requestId = ++requestSeqRef.current;
    setLoading(true);

    const timer = setTimeout(() => {
      void (async () => {
        try {
          const fresh = await fetcherRef.current();
          if (requestSeqRef.current !== requestId) return;
          cacheRef.current.set(cacheKey, fresh);
          setResults(fresh);
        } catch (error) {
          if (requestSeqRef.current !== requestId) return;
          onErrorRef.current?.(error);
          setResults([]);
        } finally {
          if (requestSeqRef.current === requestId) {
            setLoading(false);
          }
        }
      })();
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [enabled, cacheKey, debounceMs]);

  return { results, loading };
}
